import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import ModuleHeader from '@components/ModuleHeader';
import Pagination from '@components/custom/Pagination';
import Spinner from '@components/custom/Spinner';
import Table from '@components/custom/Table';

import { crudActions } from '@sm/services/crudActions';
import { showAlert } from '@sm/store/alertSlice';
import { formatBalance } from '@sm/utils/utils';

const LatestWithdrawals = ({ selectedCurrency, isOtc, shouldRefresh }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);

  const [withdrawals, setWithdrawals] = useState([]);
  const [cnt, setCnt] = useState(0);
  const perPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const columns = useMemo(() => [
    {
      value: 'requestedDate',
      label: 'Request Date',
      leftAligned: true,
      isNumber: false
    }, {
      value: 'requestedWithdrawal',
      label: 'Requested Withdrawal',
      rightAligned: true,
      isNumber: true
    }, {
      value: 'amount',
      label: 'Amount Withdrawn',
      rightAligned: true,
      isNumber: true
    }, {
      value: 'fees',
      label: 'Withdrawal Fee',
      rightAligned: true,
      isNumber: true
    }, {
      value: 'status',
      label: 'Status',
      rightAligned: true,
      isNumber: false
    }
  ], []);

  useEffect(() => {
    if (!selectedCurrency) return;
    let isMounted = true;
    setIsLoading(true);

    crudActions.get(`v1/wallets/withdrawals?currency=${selectedCurrency}&limit=${perPage}&pageNumber=${currentPage}`)
      .then((response) => {
        if (isMounted) {
          setCnt(response?.size);
          setWithdrawals(response?.withdrawalData);
        }
      })
      .catch((err) => {
        if (isMounted) {
          dispatch(showAlert('danger', err?.message));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch, selectedCurrency, currentPage, location, shouldRefresh]);

  const handleChangePage = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const getStatusLabel = (status) => {
    switch (status) {
    case 'Pending':
      return 'orange';
    case 'Approved':
      return 'green';
    case 'Cancelled':
      return 'red';
    default:
      return 'grey';
    }
  };

  const prepareTableData = useMemo(() => {
    return withdrawals.map(elem => {
      return {
        ...elem,
        amount: formatBalance(elem.amount),
        requestedWithdrawal: formatBalance(elem.requestedWithdrawal),
        status: (
          <span className={ `status ${getStatusLabel(elem.status)}` }> { elem.status } </span>
        )
      };
    });
  }, [withdrawals]);

  const totalPageCount = Math.ceil(cnt / perPage);

  return (
    <div className='component shadow mb-4 position-relative settlement'>
      <ModuleHeader
        id='article'
        title='Latest Withdrawals'
      />

      {isLoading && <Spinner /> }

      <Table
        className='mt-3 overflow-table'
        columns={ columns }
        data={ prepareTableData }
        disableSort={ true }
      />

      {totalPageCount > 1 && (
        <div className="pagination">
          <Pagination
            currentPage={ Number(currentPage) }
            totalPages={ totalPageCount }
            onPageChange={ handleChangePage }
          />
        </div>
      )}
    </div>
  );
};

export default LatestWithdrawals;